import React, { useState, useEffect, useCallback } from 'react'
import { Grid, InputLabel, FormControlLabel } from '@material-ui/core'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { useParams, navigate, useLocation } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { Loader } from '@refera/ui-web'

import { EMAIL_REGEX, PHONE_REGEX, ROUTES } from '_/utils/constants'
import HeaderTitle from '_/components/header-title'
import Loading from '_/components/loading'
import { BasicInput } from '_/components/inputs'
import useStyles from '_/views/agency-user/styles'
import NavbarButtons from '_/views/agency-user/navbar-buttons'
import { Checkbox } from '@mui/material'

import { addProviderStaffUser, updateProviderStaffUser } from '_/modules/provider/actions'
import { providerSelector } from '_modules/provider/selectors'
import { getProviderStaffUsers, getCompanyUsers } from '_/services/provider'
import { formatPhoneValueMask } from '_/utils/helpers'

const ProviderUser = () => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { providerId, userId } = useParams()
  const { showToast } = useToast()
  const { state } = useLocation()

  const provider = useSelector(providerSelector)

  const incoming = state?.providerUser
  const [user, setUser] = useState(incoming || {})
  const [pageLoading, setPageLoading] = useState(true)
  const [requestLoading, setRequestLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      name: incoming?.name || '',
      email: incoming?.email || '',
      phone: incoming?.phone || '',
      isActive: incoming?.isActive ?? true,
      isAdmin: incoming?.isAdmin ?? false,
    },
  })
  const { handleSubmit, control, formState, reset, setValue, getValues } = methods
  const { isDirty } = formState

  const formatLocalPhone = useCallback(value => {
    const digits = String(value || '').replace(/[^\d]/g, '')
    const localDigits = digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits
    return formatPhoneValueMask(localDigits)
  }, [])

  const mapUserData = useCallback(data => {
    return {
      ...data,
      name: data?.personResponsibleName || data?.person_responsible_name || data?.name || '',
      phone: data?.phone || data?.telefone || '',
      email: data?.email || '',
      isActive: data?.isActive ?? data?.is_active ?? true,
      isAdmin: data?.isAdmin ?? data?.is_admin ?? false,
    }
  }, [])

  const updateFormState = useCallback(
    userData => {
      setUser(userData)
      const formattedPhone = formatLocalPhone(userData.phone)

      setValue('name', userData.name)
      setValue('email', userData.email)
      setValue('phone', formattedPhone)
      setValue('isActive', userData.isActive)
      setValue('isAdmin', userData.isAdmin)

      reset({
        name: userData.name,
        email: userData.email,
        phone: formattedPhone,
        isActive: userData.isActive,
        isAdmin: userData.isAdmin,
      })
      setPageLoading(false)
    },
    [formatLocalPhone, reset, setValue]
  )

  const handleNavigate = () => {
    navigate(`${ROUTES.PROVIDER}/${providerId}`)
  }

  const handleSave = useCallback(
    async data => {
      try {
        setRequestLoading(true)
        const payload = { ...data, phone: data.phone.replace(/[^\d]/g, '') }
        if (!userId) {
          await dispatch(addProviderStaffUser(providerId, payload))
          showToast({ message: 'Usuário salvo com sucesso.', type: 'success' })
          handleNavigate()
        } else {
          await dispatch(updateProviderStaffUser(providerId, userId, payload))
          showToast({ message: 'Usuário salvo com sucesso.', type: 'success' })
          handleNavigate()
        }
      } catch (err) {
        showToast({ message: err?.detail || 'Ocorreu um erro ao salvar o usuário.', type: 'error' })
      } finally {
        setRequestLoading(false)
      }
    },
    [dispatch, providerId, userId]
  )

  useEffect(() => {
    getCompanyUsers('provider_user_list')(providerId)
      .then(data => {
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []
        const found = list.find(u => String(u.id) === String(userId))

        if (found) {
          const userData = mapUserData(found)
          updateFormState(userData)
        } else if (incoming) {
          // Fallback para usar dados da navegação se não encontrar na lista
          // incoming já deve estar formatado, mas mapUserData garante consistência
          const userData = mapUserData(incoming)
          updateFormState(userData)
        } else {
          // Fallback final para buscar como staff user
          getProviderStaffUsers('provider_user')(providerId, userId || undefined)
            .then(staffData => {
              const userData = mapUserData(staffData)
              updateFormState(userData)
            })
            .finally(() => setPageLoading(false))
        }
      })
      .catch(() => {
        if (incoming) {
          const userData = mapUserData(incoming)
          updateFormState(userData)
        } else {
          getProviderStaffUsers('provider_user')(providerId, userId || undefined)
            .then(staffData => {
              const userData = mapUserData(staffData)
              updateFormState(userData)
            })
            .finally(() => setPageLoading(false))
        }
      })
  }, [providerId, userId, state, reset, setValue, mapUserData, updateFormState])

  if ((pageLoading && userId) || (!user?.id && userId)) {
    return <Loading />
  }

  return (
    <Grid className={styles.main}>
      <FormProvider {...methods}>
        <HeaderTitle
          title={`Cadastro do prestador / ${provider?.name || ''}`}
          backButtonAction={handleNavigate}
        >
          <NavbarButtons
            hideResend
            isEditing={isEditing}
            isLoading={requestLoading}
            setIsEditing={setIsEditing}
            user={user}
            hasChanges={isDirty}
            formId="provider-user-form"
            disableEdit
            hideEdit
            onCancel={() => {
              reset({
                name: user?.name || '',
                email: user?.email || '',
                phone: user?.phone || '',
                isActive: user?.isActive || true,
                isAdmin: user?.isAdmin || false,
              })
            }}
          />
        </HeaderTitle>
        {/* TODO: Implementar seleção quando prestador possuir mais de um cadastro vinculado */}
        <form
          id="provider-user-form"
          onSubmit={handleSubmit(handleSave)}
          className={styles.container}
        >
          <div className={styles.subcontainer}>
            <InputLabel className={styles.label}>Dados principais</InputLabel>
            <div className={styles.wrapper}>
              <Grid className={styles.input}>
                <BasicInput
                  label="Nome completo"
                  name="name"
                  labelClasses={styles.inputLabel}
                  required
                  disabled
                  defaultValue={user?.name || getValues('name') || ''}
                />
              </Grid>
              <Grid className={styles.input}>
                <BasicInput
                  label="E-mail"
                  name="email"
                  required
                  labelClasses={styles.inputLabel}
                  disabled
                  defaultValue={user?.email || getValues('email') || ''}
                  rules={{
                    pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
                    required: { value: true, message: 'Esse campo é obrigatório' },
                  }}
                />
              </Grid>
              <Grid className={styles.input}>
                <BasicInput
                  label="Telefone"
                  name="phone"
                  required
                  labelClasses={styles.inputLabel}
                  disabled
                  defaultValue={formatLocalPhone(user?.phone || getValues('phone')) || ''}
                  rules={{
                    pattern: { value: PHONE_REGEX, message: 'Telefone inválido' },
                  }}
                />
              </Grid>

              <div className={styles.checkboxField}>
                <Grid className={styles.input}>
                  <Controller
                    name="isActive"
                    control={control}
                    defaultValue={user?.isActive || true}
                    render={({ value }) => {
                      return (
                        <FormControlLabel
                          label="Ativo"
                          control={
                            <Checkbox
                              className={styles.checkbox}
                              checked={value}
                              disabled
                              defaultChecked={user?.isActive}
                            />
                          }
                        />
                      )
                    }}
                  />
                </Grid>
              </div>
            </div>
          </div>

          <div className={styles.subcontainer}>
            <InputLabel className={styles.label}>Permissões</InputLabel>
            <Grid className={styles.wrapper}>
              <div className={styles.adminField}>
                <Grid className={styles.input}>
                  <Controller
                    name="isAdmin"
                    control={control}
                    defaultValue={user?.isAdmin}
                    render={({ value }) => {
                      return (
                        <FormControlLabel
                          label="Admin"
                          control={
                            <Checkbox
                              className={styles.checkbox}
                              checked={value}
                              disabled
                              defaultChecked={user?.isAdmin}
                            />
                          }
                        />
                      )
                    }}
                  />
                </Grid>
              </div>
            </Grid>
          </div>

          {requestLoading && <Loader hasBackdrop open={requestLoading} label="Aguarde..." />}
        </form>
      </FormProvider>
    </Grid>
  )
}

export default ProviderUser
